import type { QueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import supabase from "@/lib/__shared__/supabase/client";
import { appStore } from "./stores/appStore";

export type ResetPasswordOptions = {
	newPassword: string;
	isOldAuth?: boolean;
	challengeToken?: string;
	resetToken?: string | null;
};

export const PASSWORD_RECOVERY_SESSION_MISSING_MESSAGE =
	"This password reset link is invalid or has expired. Please request a new one.";

export class PasswordRecoverySessionMissingError extends Error {
	constructor() {
		super(PASSWORD_RECOVERY_SESSION_MISSING_MESSAGE);
		this.name = "PasswordRecoverySessionMissingError";
	}
}

export interface IAuthManager {
	appStore: typeof appStore;
	queryClient: QueryClient | undefined;
	initialize: ({ queryClient }: { queryClient: QueryClient }) => Promise<void>;
	addToNewsletter: (email: string) => Promise<void>;
	deleteAccount: (_id: string, _accessToken: string) => Promise<void>;
	hasPasswordRecoverySession: () => Promise<boolean>;
	requestPasswordReset: (email: string, captchaToken?: string) => Promise<void>;
	resetEmail: (email: string) => Promise<void>;
	resetPassword: (options: ResetPasswordOptions) => Promise<void>;
	signInWithEmail: (
		email: string,
		password: string,
		challengeToken: string,
	) => Promise<boolean>;
	signOut: () => Promise<void>;
	signUpWithEmail: (
		email: string,
		password: string,
		captchaToken: string,
		visitorId?: string,
	) => Promise<void>;
}

// Frontend expects Errors to be thrown with a message property

export const AuthManager: IAuthManager = {
	appStore: appStore,
	queryClient: undefined,
	async initialize({ queryClient }) {
		AuthManager.queryClient = queryClient;
		const set = appStore.getState();

		set.setAuthLoading(true);

		const { data: sessionData, error: sessionError } =
			await supabase.auth.getSession();

		if (sessionError) {
			set.setAuthLoading(false);
			Sentry.captureException(sessionError);
			throw new Error(sessionError.message);
		}

		await set.setSession(sessionData.session);
		set.setAuthLoading(false);

		// Subscribe to future changes
		supabase.auth.onAuthStateChange((_event, session) => {
			// don't await, will cause recursive loop
			set.setSession(session);
		});
	},
	async addToNewsletter(email: string): Promise<void> {
		try {
			const response = await fetch(
				`${import.meta.env.VITE_USER_SERVER_URL}/user/subscribe-newsletter`,
				{
					method: "POST",
					headers: {
						"Content-type": "application/json; charset=UTF-8",
					},
					body: JSON.stringify({ email }),
				},
			);

			if (response.status === 500) {
				throw new Error(
					"We apologize, but an error occurred. Please try again later.",
				);
			}

			const json: { success: boolean; message: string } = await response.json();

			if (!json.success) {
				throw new Error(json.message ?? "Failed to subscribe to newsletter.");
			}
		} catch (_) {
			return Promise.resolve(); // Silently fail, we'll catch this later manually API will log it
		}
	},
	async deleteAccount(_id: string, _accessToken: string): Promise<void> {
		try {
			const response = await fetch(
				`${import.meta.env.VITE_USER_SERVER_URL}/user/${_id}`,
				{
					method: "DELETE",
					headers: {
						"Content-type": "application/json; charset=UTF-8",
						Authorization: `Bearer ${_accessToken}`,
					},
				},
			);

			const json: { data: object; message: string } = await response.json();

			if (response.ok) {
				return;
			}

			throw new Error(json.message || "Failed to delete account.");
		} catch (error) {
			Sentry.captureException(error);
			return Promise.reject(
				error instanceof Error
					? error.message
					: "An unexpected error occurred.",
			);
		}
	},
	async hasPasswordRecoverySession(): Promise<boolean> {
		const { data, error } = await supabase.auth.getSession();

		if (error) {
			Sentry.captureException(error);
			return false;
		}

		return Boolean(data.session);
	},
	// this needs to go to the user server to check if the user is in the old or new database
	async requestPasswordReset(
		email: string,
		captchaToken?: string,
	): Promise<void> {
		const set = appStore.getState();
		try {
			set.setAuthLoading(true);

			const response = await fetch(
				`${import.meta.env.VITE_USER_SERVER_URL}/auth/forgot-password`,
				{
					method: "POST",
					headers: {
						"Content-type": "application/json; charset=UTF-8",
					},
					credentials: "include", // Important to include cookies
					body: JSON.stringify({
						email,
						challengeToken: captchaToken,
					}),
				},
			);

			if (response.status === 500) {
				throw new Error(
					"We apologize, but an error occurred. Please try again later.",
				);
			}

			const json: {
				success: boolean;
				data: { accessToken: string; refreshToken: string };
				message: string;
			} = await response.json();

			if (!json.success) {
				set.setAuthLoading(false);
				throw new Error(json.message ?? "Failed to send reset email.");
			}
		} catch (error) {
			return Promise.reject(error);
		} finally {
			set.setAuthLoading(false);
		}
	},
	async resetEmail(email: string): Promise<void> {
		const set = appStore.getState();
		try {
			set.setAuthLoading(true);

			const accessToken = appStore.getState().session?.access_token;
			if (!accessToken) {
				throw new Error("Unauthorized.");
			}

			const response = await fetch(
				`${import.meta.env.VITE_USER_SERVER_URL}/auth/change-email`,
				{
					method: "POST",
					headers: {
						"Content-type": "application/json; charset=UTF-8",
						Authorization: `Bearer ${accessToken}`,
					},
					credentials: "include",
					body: JSON.stringify({
						email,
					}),
				},
			);

			if (response.status >= 500) {
				throw new Error(
					"We apologize, but an error occurred. Please try again later.",
				);
			}

			const json: {
				success: boolean;
				message: string;
			} = await response.json();

			if (!json.success) {
				throw new Error(json.message ?? "Failed to request email change.");
			}
		} catch (error) {
			if (error instanceof Error) {
				Sentry.captureException(error);
				throw error;
			}

			throw new Error("Failed to request email change.");
		} finally {
			set.setAuthLoading(false);
		}
	},
	async resetPassword(options: ResetPasswordOptions): Promise<void> {
		const set = appStore.getState();
		try {
			const { newPassword, isOldAuth, challengeToken, resetToken } = options;
			set.setAuthLoading(true);

			if (isOldAuth) {
				const response = await fetch(
					`${import.meta.env.VITE_USER_SERVER_URL}/auth/reset-password`,
					{
						method: "POST",
						headers: {
							"Content-type": "application/json; charset=UTF-8",
						},
						credentials: "include", // Important to include cookies
						body: JSON.stringify({
							password: newPassword,
							token: resetToken,
							challengeToken,
						}),
					},
				);

				if (response.status === 500) {
					throw new Error(
						"We apologize, but an error occurred. Please try again later.",
					);
				}

				const json: {
					success: boolean;
					data: { accessToken: string; refreshToken: string };
					message: string;
				} = await response.json();

				if (!json.success) {
					set.setAuthLoading(false);
					throw new Error(json.message ?? "Failed to sign in.");
				}

				const { data: sessionData, error: sessionError } =
					await supabase.auth.setSession({
						access_token: json.data.accessToken,
						refresh_token: json.data.refreshToken,
					});

				if (sessionError) {
					set.setAuthLoading(false);
					Sentry.captureException(sessionError);
					throw new Error(sessionError.message);
				}

				await set.setSession(sessionData.session);
			} else {
				const hasRecoverySession =
					await AuthManager.hasPasswordRecoverySession();

				if (!hasRecoverySession) {
					throw new PasswordRecoverySessionMissingError();
				}

				const { error } = await supabase.auth.updateUser({
					password: newPassword,
				});

				if (error) {
					set.setAuthLoading(false);
					Sentry.captureException(error);
					if (
						error.name === "AuthSessionMissingError" ||
						error.message === "Auth session missing!"
					) {
						throw new PasswordRecoverySessionMissingError();
					}
					throw new Error(error.message);
				}
			}
		} catch (error) {
			return Promise.reject(error);
		} finally {
			set.setAuthLoading(false);
		}
	},
	// Returns true if the user doesn't need to verify their email or false if they do, otherwise error
	async signInWithEmail(
		email: string,
		password: string,
		challengeToken: string,
	): Promise<boolean> {
		const set = appStore.getState();

		try {
			set.setAuthLoading(true);

			const response = await fetch(
				`${import.meta.env.VITE_USER_SERVER_URL}/auth/sign-in`,
				{
					method: "POST",
					headers: {
						"Content-type": "application/json; charset=UTF-8",
					},
					credentials: "include", // Important to include cookies
					body: JSON.stringify({
						email,
						password,
						challengeToken,
					}),
				},
			);

			if (response.status >= 500) {
				throw new Error(
					"We apologize, but an error occurred. Please try again later.",
				);
			}

			if (response.status === 403) {
				// TODO: we should probably create an AppError class that extends Error and has a status property
				throw new Error("VERIFICATION_REQUIRED");
			}

			const json: {
				success: boolean;
				data: {
					accessToken: string;
					refreshToken: string;
					isAccountVerified: boolean;
				};
				message: string;
			} = await response.json();

			if (!json.success) {
				set.setAuthLoading(false);
				throw new Error(json.message ?? "Failed to sign in.");
			}

			if (json.data.isAccountVerified) {
				const { data: sessionData, error: sessionError } =
					await supabase.auth.setSession({
						access_token: json.data.accessToken,
						refresh_token: json.data.refreshToken,
					});

				if (sessionError) {
					set.setAuthLoading(false);
					Sentry.captureException(sessionError);
					throw new Error(sessionError.message);
				}

				await set.setSession(sessionData.session);
				return true; // User doesn't need to verify their email
			}

			return false; // User needs to verify their email
		} catch (error) {
			return Promise.reject(error);
		} finally {
			set.setAuthLoading(false);
		}
	},
	async signOut() {
		const store = appStore.getState();

		store.setAuthLoading(true);

		await supabase.auth.signOut();

		AuthManager.queryClient!.clear();
		store.setSession(null); // no need to await this
		store.setSubscription(null);
		store.setAuthLoading(false);
	},
	async signUpWithEmail(
		email: string,
		password: string,
		challengeToken: string,
		visitorId?: string,
	): Promise<void> {
		const set = appStore.getState();

		try {
			set.setAuthLoading(true);

			const response = await fetch(
				`${import.meta.env.VITE_USER_SERVER_URL}/auth/sign-up`,
				{
					method: "POST",
					headers: {
						"Content-type": "application/json; charset=UTF-8",
					},
					credentials: "include", // Important to include cookies
					body: JSON.stringify({
						email,
						password,
						challengeToken,
						// Lets the API bind this Visitor's prior Attribution Touches to
						// the new Coffitivity User. The browser boundary owns cookie access
						// and passes this value in because the cookie is host-only.
						visitorId,
					}),
				},
			);

			if (response.status >= 500) {
				throw new Error(
					"We apologize, but an error occurred. Please try again later.",
				);
			}

			const json: {
				success: boolean;
				message: string;
			} = await response.json();

			if (!json.success) {
				throw new Error(json.message ?? "Failed to sign in.");
			}

			return;
		} catch (error) {
			return Promise.reject(error);
		} finally {
			set.setAuthLoading(false);
		}
	},
};
